import { useEffect, useState } from "react";
import { ChartLineUp, CurrencyDollar, Timer, CheckCircle, XCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CAPABILITIES = ["", "text", "image"];
const STATUSES = ["", "success", "failed"];
const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(4)}`);
const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

function Stat({ icon: Icon, label, value, testid, tone = "" }) {
  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-1"><Icon size={15} /><span className="text-xs">{label}</span></div>
      <p className={`text-2xl font-heading tabular-nums ${tone}`}>{value}</p>
    </div>
  );
}

export default function UsageTab() {
  const { currentOrg } = useOrg();
  const [data, setData] = useState(null);
  const [budget, setBudget] = useState(null);
  const [filters, setFilters] = useState({ start: "", end: "", capability: "", provider: "", model: "", status: "" });
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/ai/kernel/usage", { params }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
    api.get("/ai/kernel/budget").then((r) => setBudget(r.data)).catch(() => {});
  };
  useEffect(() => { if (currentOrg) load(); /* eslint-disable-next-line */ }, [currentOrg]);

  const t = data?.totals || {};

  return (
    <div className="mt-4 space-y-6" data-testid="ai-usage-tab">
      {/* Budget banner */}
      {budget && (
        <div className={`border rounded-sm p-4 flex items-center justify-between ${budget.allowed ? "border-border bg-card" : "border-destructive bg-destructive/10"}`} data-testid="ai-budget-banner">
          <div className="flex items-center gap-3">
            {budget.allowed ? <CheckCircle size={20} className="text-primary" /> : <XCircle size={20} className="text-destructive" />}
            <div>
              <p className="text-sm font-medium" data-testid="ai-budget-state">{budget.allowed ? "Within AI budget" : "AI budget exceeded — executions paused"}</p>
              <p className="text-xs text-muted-foreground">
                Monthly: {money(budget.monthly.spend)}{budget.monthly.limit ? ` / ${money(budget.monthly.limit)}` : " (no limit)"} ·{" "}
                Daily: {money(budget.daily.spend)}{budget.daily.limit ? ` / ${money(budget.daily.limit)}` : " (no limit)"} · <span className="italic">estimated</span>
              </p>
            </div>
          </div>
          {budget.warnings?.length > 0 && budget.allowed && (
            <Badge variant="outline" className="rounded-sm text-[10px] border-amber-500 text-amber-600" data-testid="ai-budget-warning"><Warning size={12} className="mr-1" />{budget.warnings.includes("warn_85") ? "85% of budget used" : "70% of budget used"}</Badge>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-card border border-border rounded-sm p-4 grid sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end" data-testid="ai-usage-filters">
        <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="rounded-sm" data-testid="usage-filter-start" /></div>
        <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="rounded-sm" data-testid="usage-filter-end" /></div>
        <div className="space-y-1"><Label className="text-xs">Capability</Label>
          <Select value={filters.capability || "all"} onValueChange={(v) => setFilters({ ...filters, capability: v === "all" ? "" : v })}>
            <SelectTrigger className="rounded-sm" data-testid="usage-filter-capability"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{CAPABILITIES.filter(Boolean).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label className="text-xs">Provider</Label><Input value={filters.provider} onChange={(e) => setFilters({ ...filters, provider: e.target.value })} placeholder="e.g. openai" className="rounded-sm" data-testid="usage-filter-provider" /></div>
        <div className="space-y-1"><Label className="text-xs">Status</Label>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
            <SelectTrigger className="rounded-sm" data-testid="usage-filter-status"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{STATUSES.filter(Boolean).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={load} disabled={loading} className="rounded-sm" data-testid="usage-apply-filters">{loading ? "Loading…" : "Apply"}</Button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat icon={ChartLineUp} label="Requests" value={t.requests ?? 0} testid="usage-total-requests" />
        <Stat icon={CheckCircle} label="Successful" value={t.successful ?? 0} testid="usage-total-success" tone="text-primary" />
        <Stat icon={XCircle} label="Failed" value={t.failed ?? 0} testid="usage-total-failed" tone={t.failed ? "text-destructive" : ""} />
        <Stat icon={CheckCircle} label="Success rate" value={pct(t.success_rate)} testid="usage-success-rate" />
        <Stat icon={CurrencyDollar} label="Est. cost" value={money(t.estimated_cost)} testid="usage-total-cost" />
        <Stat icon={Timer} label="Avg latency" value={t.avg_latency_ms != null ? `${t.avg_latency_ms}ms` : "—"} testid="usage-avg-latency" />
      </div>

      {/* Breakdown tables */}
      <div className="grid lg:grid-cols-2 gap-6">
        <BreakdownTable title="Cost & usage by provider" rows={data?.by_provider} keyName="provider" testid="usage-by-provider" />
        <BreakdownTable title="Cost & usage by capability" rows={data?.by_capability} keyName="capability" testid="usage-by-capability" />
      </div>

      {/* Recent executions */}
      <div className="bg-card border border-border rounded-sm overflow-x-auto" data-testid="usage-recent">
        <div className="px-4 py-3 border-b border-border text-sm font-medium">Recent executions</div>
        <Table>
          <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Capability</TableHead><TableHead>Provider</TableHead><TableHead>Model</TableHead><TableHead>Cred. source</TableHead><TableHead>Latency</TableHead><TableHead>Cost</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {(data?.recent || []).map((r, i) => (
              <TableRow key={i} data-testid={`usage-recent-row-${i}`}>
                <TableCell className="text-xs text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm text-[10px]">{r.capability}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{r.provider || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{r.model || "—"}</TableCell>
                <TableCell className="font-mono text-[10px] text-muted-foreground">{r.credential_source || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{r.execution_time_ms != null ? `${r.execution_time_ms}ms` : "—"}</TableCell>
                <TableCell className="font-mono text-xs" data-testid={`usage-recent-cost-${i}`}>
                  {r.cost_basis === "credits"
                    ? <span title="Credit-based estimate — not a monetary charge">{r.estimated_cost ?? 0} <span className="text-[10px] text-muted-foreground">cr est.</span></span>
                    : money(r.estimated_cost)}
                </TableCell>
                <TableCell><Badge variant={r.success === false ? "destructive" : "default"} className="rounded-sm text-[10px]">{r.success === false ? (r.error_class || "failed") : "ok"}</Badge></TableCell>
              </TableRow>
            ))}
            {(!data?.recent || data.recent.length === 0) && (
              <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">No AI executions yet for this organization.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function BreakdownTable({ title, rows, keyName, testid }) {
  return (
    <div className="bg-card border border-border rounded-sm overflow-x-auto" data-testid={testid}>
      <div className="px-4 py-3 border-b border-border text-sm font-medium">{title}</div>
      <Table>
        <TableHeader><TableRow><TableHead>{keyName}</TableHead><TableHead>Requests</TableHead><TableHead>Failed</TableHead><TableHead>Avg latency</TableHead><TableHead>Est. cost</TableHead></TableRow></TableHeader>
        <TableBody>
          {(rows || []).map((r, i) => (
            <TableRow key={i} data-testid={`${testid}-row-${r[keyName] || i}`}>
              <TableCell className="font-mono text-xs">{r[keyName] || "—"}</TableCell>
              <TableCell className="tabular-nums">{r.requests}</TableCell>
              <TableCell className={`tabular-nums ${r.failed ? "text-destructive" : ""}`}>{r.failed}</TableCell>
              <TableCell className="font-mono text-xs">{r.avg_latency_ms}ms</TableCell>
              <TableCell className="font-mono text-xs">{money(r.cost)}</TableCell>
            </TableRow>
          ))}
          {(!rows || rows.length === 0) && (<TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">No data.</TableCell></TableRow>)}
        </TableBody>
      </Table>
    </div>
  );
}
